// src/pages/TeamPerformancePage.js
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useApi } from '../hooks/useApi';
import {
  ArrowLeft, Activity, Users, Clock, CheckCircle2,
  AlertCircle, Shield, Filter, Search, Calendar,
  ChevronDown, TrendingUp, BarChart2, PieChart as PieChartIcon,
  ArrowUpRight, ArrowDownRight, Minus
} from 'lucide-react';
import {
  AreaChart, Area, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.35, ease: 'easeOut' } },
};

const stagger = {
  visible: { transition: { staggerChildren: 0.08 } },
};

// Status Colors
const STATUS_COLORS = {
  'Open': '#3b82f6',
  'In Progress': '#8b5cf6',
  'On Hold': '#f59e0b',
  'Resolved': '#10b981',
  'Closed': '#14b8a6',
  'Reopened': '#ef4444'
};

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6'];

function formatDuration(hours) {
  if (hours === null || isNaN(hours)) return '—';
  if (hours === 0) return '0h';
  const totalMins = Math.round(hours * 60);
  const d = Math.floor(totalMins / 1440);
  const h = Math.floor((totalMins % 1440) / 60);
  const m = totalMins % 60;
  let s = '';
  if (d > 0) s += d + 'd ';
  if (h > 0) s += h + 'h ';
  if (m > 0 && d === 0) s += m + 'm';
  return s.trim() || '0h';
}

function Skeleton({ width, height, borderRadius = 8, className = '' }) {
  return (
    <div
      className={className}
      style={{
        width,
        height,
        borderRadius,
        background: 'linear-gradient(90deg, var(--white-05) 25%, var(--white-10) 50%, var(--white-05) 75%)',
        backgroundSize: '200% 100%',
        animation: 'shimmer 1.5s infinite'
      }}
    />
  );
}

function TrendIndicator({ current, previous, inverse = false }) {
  if (current === null || previous === null || previous === 0) return <span style={{ color: 'var(--text-muted)', fontSize: 12 }}><Minus size={12} style={{ display: 'inline', verticalAlign: 'middle' }}/> —</span>;
  
  const diff = current - previous;
  if (diff === 0) return <span style={{ color: 'var(--text-muted)', fontSize: 12 }}><Minus size={12} style={{ display: 'inline', verticalAlign: 'middle' }}/> No change</span>;
  
  const percent = Math.abs(Math.round((diff / previous) * 100));
  const isPositive = diff > 0;
  
  // If inverse is true (e.g., resolution time), lower is better
  const isGood = inverse ? !isPositive : isPositive;
  const color = isGood ? '#10b981' : '#ef4444';
  const Icon = isPositive ? ArrowUpRight : ArrowDownRight;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, color }}>
      <Icon size={14} />
      <span style={{ fontSize: 12, fontWeight: 700 }}>{percent}%</span>
      <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>vs prev</span>
    </div>
  );
}

function KpiCard({ title, value, previous, subtitle, icon: Icon, status, suffix = '', inverseTrend = false, loading, onClick }) {
  let indicatorColor = 'var(--text-muted)';
  let statusText = status || 'Neutral';
  
  if (status === 'Excellent') indicatorColor = '#10b981';
  if (status === 'Good') indicatorColor = '#3b82f6';
  if (status === 'Needs Attention') indicatorColor = '#f59e0b';
  if (status === 'Overloaded' || status === 'Critical') indicatorColor = '#ef4444';

  return (
    <motion.div
      variants={fadeUp}
      whileHover={onClick ? { y: -4, boxShadow: '0 10px 30px -10px rgba(0,0,0,0.5)' } : {}}
      onClick={onClick}
      style={{
        background: 'var(--surface)',
        padding: '24px',
        borderRadius: '16px',
        border: '1px solid var(--white-05)',
        backdropFilter: 'blur(16px)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        position: 'relative',
        overflow: 'hidden',
        cursor: onClick ? 'pointer' : 'default'
      }}
    >
      <div style={{ position: 'absolute', top: -20, right: -20, opacity: 0.05, color: indicatorColor }}>
        <Icon size={120} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {title}
        </div>
        <div style={{ background: 'var(--black-30)', padding: 10, borderRadius: 10, color: indicatorColor }}>
          <Icon size={20} strokeWidth={2} />
        </div>
      </div>
      <div>
        {loading ? (
          <Skeleton width="60%" height={40} />
        ) : (
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
            <span style={{ fontSize: 36, fontWeight: 800, color: 'var(--text-primary)', lineHeight: 1 }}>
              {value !== null ? value : '—'}
            </span>
            {value !== null && suffix && <span style={{ fontSize: 18, fontWeight: 600, color: 'var(--text-muted)' }}>{suffix}</span>}
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12 }}>
          {loading ? (
            <Skeleton width="40%" height={16} />
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {statusText !== 'Neutral' && <div style={{ width: 8, height: 8, borderRadius: '50%', background: indicatorColor }} />}
                <span style={{ fontSize: 12, fontWeight: 600, color: indicatorColor }}>{statusText !== 'Neutral' ? statusText : ''}</span>
              </div>
              <TrendIndicator current={value} previous={previous} inverse={inverseTrend} />
            </>
          )}
        </div>
      </div>
    </motion.div>
  );
}

export default function TeamPerformancePage({ showToast }) {
  const { request, loading } = useApi();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  
  // Filters
  const [timeRange, setTimeRange] = useState('month'); 
  const [customRange, setCustomRange] = useState({ start: '', end: '' });
  const [sortBy, setSortBy] = useState('tickets_closed'); 
  const [selectedEngineer, setSelectedEngineer] = useState('all');

  useEffect(() => {
    // Inject shimmer style dynamically if not present
    if (!document.getElementById('shimmer-style')) {
      const style = document.createElement('style');
      style.id = 'shimmer-style';
      style.innerHTML = `
        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  useEffect(() => {
    fetchPerformanceData();
  }, [timeRange, sortBy, selectedEngineer, customRange]);

  const fetchPerformanceData = async () => {
    try {
      // Avoid firing if custom is selected but dates are empty
      if (timeRange === 'custom' && (!customRange.start || !customRange.end)) return;
      
      let url = `/api/admin/performance?timeRange=${timeRange}&sortBy=${sortBy}&engineer=${selectedEngineer}`;
      if (timeRange === 'custom') {
        url += `&start=${customRange.start}&end=${customRange.end}`;
      }
      
      const res = await request(url);
      setData(res);
    } catch (err) {
      showToast('Failed to load performance data', 'error');
    }
  };

  const { kpis, prev_kpis, engineers, charts } = data || { kpis: {}, prev_kpis: {}, engineers: [], charts: { status_distribution: [], resolution_trend: [] } };
  
  const hasData = engineers.length > 0 && kpis.total_closed !== undefined;
  const noActivity = hasData && kpis.total_closed === 0 && kpis.high_priority_pending === 0 && charts.resolution_trend?.reduce((a,b) => a + b.created, 0) === 0;

  // Render tooltip for donut chart
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', padding: '12px', borderRadius: '8px', color: 'var(--text-primary)', boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}>
          <div style={{ fontWeight: 700, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ width: 10, height: 10, borderRadius: '50%', background: STATUS_COLORS[data.name] || '#ccc' }} />
            {data.name}
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            Count: <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{data.value}</span>
          </div>
        </div>
      );
    }
    return null;
  };

  const renderDonutLegend = (props) => {
    const { payload } = props;
    const total = charts.status_distribution.reduce((acc, curr) => acc + curr.value, 0);
    
    return (
      <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexWrap: 'wrap', gap: '12px 16px', justifyContent: 'center' }}>
        {payload.map((entry, index) => {
          const percent = total > 0 ? Math.round((entry.payload.value / total) * 100) : 0;
          return (
            <li key={`item-${index}`} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--text-secondary)' }}>
              <div style={{ width: 10, height: 10, borderRadius: '2px', background: entry.color }} />
              <span style={{ fontWeight: 600 }}>{entry.value}</span>
              <span style={{ color: 'var(--text-muted)' }}>({percent}%)</span>
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <motion.div 
      initial="hidden"
      animate="visible"
      variants={stagger}
      style={{ width: '100%', boxSizing: 'border-box', background: 'transparent', minHeight: '100vh', padding: '24px' }}
    >
      {/* ── HEADER & CONTROLS ────────────────────────────────────────── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 32, flexWrap: 'wrap', gap: 20 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
            <button 
              onClick={() => navigate('/admin')}
              style={{
                background: 'var(--white-05)', border: 'none', borderRadius: '8px',
                padding: '8px', color: 'var(--text-secondary)', cursor: 'pointer', transition: 'all 0.2s'
              }}
            >
              <ArrowLeft size={18} />
            </button>
            <h1 style={{ margin: 0, fontSize: '1.8rem', fontWeight: 800, color: 'var(--text-primary)', letterSpacing: '-0.02em', display: 'flex', alignItems: 'center', gap: 10 }}>
              <Activity color="var(--brand-primary)" /> Team Performance
            </h1>
          </div>
          <p style={{ margin: 0, fontSize: 14, color: 'var(--text-muted)', marginLeft: 46 }}>
            Enterprise analytics, workload tracking, and SLA monitoring.
          </p>
        </div>

        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <Calendar size={16} color="var(--text-muted)" style={{ position: 'absolute', left: 12 }} />
            <select 
              value={timeRange} 
              onChange={e => setTimeRange(e.target.value)}
              style={{ background: 'var(--surface)', border: '1px solid var(--white-06)', padding: '10px 16px 10px 36px', borderRadius: '10px', color: 'var(--text-primary)', fontSize: 13, fontWeight: 600, outline: 'none', cursor: 'pointer', appearance: 'none', paddingRight: 32 }}
            >
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="year">This Year</option>
              <option value="all">All Time</option>
              {/* <option value="custom">Custom Range</option> */}
            </select>
            <ChevronDown size={14} color="var(--text-muted)" style={{ position: 'absolute', right: 12, pointerEvents: 'none' }} />
          </div>
          
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <Users size={16} color="var(--text-muted)" style={{ position: 'absolute', left: 12 }} />
            <select 
              value={selectedEngineer} 
              onChange={e => setSelectedEngineer(e.target.value)}
              style={{ background: 'var(--surface)', border: '1px solid var(--white-06)', padding: '10px 16px 10px 36px', borderRadius: '10px', color: 'var(--text-primary)', fontSize: 13, fontWeight: 600, outline: 'none', cursor: 'pointer', appearance: 'none', paddingRight: 32 }}
            >
              <option value="all">All Engineers</option>
              {engineers?.map(e => (
                <option key={e.id} value={e.id}>{e.username}</option>
              ))}
            </select>
            <ChevronDown size={14} color="var(--text-muted)" style={{ position: 'absolute', right: 12, pointerEvents: 'none' }} />
          </div>
        </div>
      </div>

      {/* ── TOP KPIS ────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 20, marginBottom: 32 }}>
        <KpiCard 
          loading={loading && !data}
          title="Avg SLA Compliance" 
          value={kpis.sla_compliance} 
          previous={prev_kpis.sla_compliance}
          suffix="%"
          subtitle="Tickets meeting SLA" 
          icon={Shield} 
          status={kpis.sla_compliance === null ? 'Neutral' : kpis.sla_compliance >= 90 ? 'Excellent' : kpis.sla_compliance >= 75 ? 'Good' : 'Needs Attention'} 
          onClick={() => navigate(`/tickets?filter=all&timeRange=${timeRange}&engineer=${selectedEngineer}`)}
        />
        <KpiCard 
          loading={loading && !data}
          title="Avg Resolution Time" 
          value={kpis.avg_resolution_hours !== null ? formatDuration(kpis.avg_resolution_hours) : null} 
          previous={prev_kpis.avg_resolution_hours !== null ? formatDuration(prev_kpis.avg_resolution_hours) : null}
          suffix=""
          subtitle="Time to resolve" 
          icon={Clock} 
          inverseTrend={true}
          status={kpis.avg_resolution_hours === null ? 'Neutral' : kpis.avg_resolution_hours <= 4 ? 'Excellent' : kpis.avg_resolution_hours <= 24 ? 'Good' : 'Needs Attention'} 
          onClick={() => navigate(`/tickets?filter=resolved&timeRange=${timeRange}&engineer=${selectedEngineer}`)}
        />
        <KpiCard 
          loading={loading && !data}
          title="Tickets Closed" 
          value={kpis.total_closed} 
          previous={prev_kpis.total_closed}
          subtitle="In selected period" 
          icon={CheckCircle2} 
          status="Neutral" 
          onClick={() => navigate(`/tickets?filter=closed&timeRange=${timeRange}&engineer=${selectedEngineer}`)}
        />
        <KpiCard 
          loading={loading && !data}
          title="High Priority Pending" 
          value={kpis.high_priority_pending} 
          previous={prev_kpis.high_priority_pending}
          subtitle="Requires attention" 
          icon={AlertCircle} 
          inverseTrend={true}
          status={kpis.high_priority_pending > 5 ? 'Critical' : kpis.high_priority_pending > 0 ? 'Needs Attention' : 'Excellent'} 
          onClick={() => navigate(`/tickets?filter=high&timeRange=${timeRange}&engineer=${selectedEngineer}`)}
        />
      </div>

      {/* ── EMPTY STATE ────────────────────────────────────────── */}
      {!loading && noActivity && (
        <motion.div variants={fadeUp} style={{ textAlign: 'center', padding: '60px 20px', background: 'var(--surface)', borderRadius: 16, border: '1px solid var(--white-05)', backdropFilter: 'blur(16px)' }}>
          <Activity size={48} color="var(--text-muted)" style={{ opacity: 0.5, marginBottom: 16 }} />
          <h3 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 8 }}>No Activity in Selected Range</h3>
          <p style={{ fontSize: 14, color: 'var(--text-secondary)' }}>There are no tickets created, resolved, or updated in this period.</p>
        </motion.div>
      )}

      {/* ── CHARTS SECTION ────────────────────────────────────────── */}
      {(!noActivity || loading) && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: 20, marginBottom: 32 }}>
          <motion.div variants={fadeUp} style={{ background: 'var(--surface)', padding: 24, borderRadius: 16, border: '1px solid var(--white-05)', backdropFilter: 'blur(16px)', flex: 2 }}>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 8 }}><TrendingUp size={18} color="var(--brand-primary)" /> Ticket Resolution Trend</h3>
            <div style={{ height: 300 }}>
              {loading && !data ? <Skeleton width="100%" height="100%" /> : (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={charts.resolution_trend}>
                    <defs>
                      <linearGradient id="colorResolved" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                      </linearGradient>
                      <linearGradient id="colorCreated" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--white-05)" vertical={false} />
                    <XAxis dataKey="date" stroke="var(--text-muted)" fontSize={12} tickLine={false} axisLine={false} />
                    <YAxis stroke="var(--text-muted)" fontSize={12} tickLine={false} axisLine={false} />
                    <Tooltip contentStyle={{ backgroundColor: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-primary)' }} />
                    <Legend iconType="circle" wrapperStyle={{ fontSize: 12, color: 'var(--text-muted)' }} />
                    <Area type="monotone" dataKey="resolved" name="Resolved" stroke="#10b981" strokeWidth={3} fillOpacity={1} fill="url(#colorResolved)" />
                    <Area type="monotone" dataKey="created" name="Created" stroke="#8b5cf6" strokeWidth={3} fillOpacity={1} fill="url(#colorCreated)" />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>
          </motion.div>

          <motion.div variants={fadeUp} style={{ background: 'var(--surface)', padding: 24, borderRadius: 16, border: '1px solid var(--white-05)', backdropFilter: 'blur(16px)', flex: 1, minWidth: 300 }}>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 8 }}><PieChartIcon size={18} color="var(--brand-primary)" /> Status Distribution</h3>
            <div style={{ height: 300 }}>
              {loading && !data ? <Skeleton width="100%" height="100%" borderRadius={150} /> : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie 
                      data={charts.status_distribution} 
                      cx="50%" cy="50%" 
                      innerRadius={70} 
                      outerRadius={100} 
                      paddingAngle={5} 
                      dataKey="value"
                      onClick={(data) => {
                        let f = 'all';
                        if (data.name === 'Open') f = 'open';
                        if (data.name === 'Closed') f = 'closed';
                        if (data.name === 'Resolved') f = 'resolved';
                        if (data.name === 'In Progress') f = 'inprogress';
                        if (data.name === 'On Hold') f = 'onhold';
                        navigate(`/tickets?filter=${f}&timeRange=${timeRange}&engineer=${selectedEngineer}`);
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      {charts.status_distribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={STATUS_COLORS[entry.name] || '#ccc'} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                    <Legend content={renderDonutLegend} verticalAlign="bottom" />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </motion.div>
        </div>
      )}

      {/* ── ENGINEER LEADERBOARD ────────────────────────────────────────── */}
      {(!noActivity || loading) && (
        <motion.div variants={fadeUp} style={{ background: 'var(--surface)', padding: 24, borderRadius: 16, border: '1px solid var(--white-05)', backdropFilter: 'blur(16px)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 16 }}>
            <h3 style={{ fontSize: 16, fontWeight: 800, color: 'var(--text-primary)', margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Users size={18} color="var(--brand-primary)" /> Engineer Performance Leaderboard
            </h3>
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <Filter size={14} color="var(--text-muted)" style={{ position: 'absolute', left: 10 }} />
                <select 
                  value={sortBy} 
                  onChange={e => setSortBy(e.target.value)}
                  style={{ background: 'var(--surface)', border: '1px solid var(--white-06)', padding: '8px 12px 8px 32px', borderRadius: '8px', color: 'var(--text-primary)', fontSize: 12, fontWeight: 600, outline: 'none', cursor: 'pointer', appearance: 'none', paddingRight: 32 }}
                >
                  <option value="tickets_closed">Sort: Most Closed</option>
                  <option value="res_rate">Sort: Highest Res Rate</option>
                  <option value="sla">Sort: Highest SLA %</option>
                  <option value="resp_time">Sort: Fastest Response</option>
                  <option value="res_time">Sort: Fastest Resolution</option>
                  <option value="reopen_rate">Sort: Lowest Reopen Rate</option>
                  <option value="active_load">Sort: Highest Backlog</option>
                  <option value="recently_active">Sort: Recently Active</option>
                </select>
                <ChevronDown size={14} color="var(--text-muted)" style={{ position: 'absolute', right: 10, pointerEvents: 'none' }} />
              </div>
            </div>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', minWidth: 800 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--white-10)' }}>
                  <th style={{ padding: '12px 16px', color: 'var(--text-muted)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Engineer</th>
                  <th style={{ padding: '12px 16px', color: 'var(--text-muted)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Tickets</th>
                  <th style={{ padding: '12px 16px', color: 'var(--text-muted)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Resolved</th>
                  <th style={{ padding: '12px 16px', color: 'var(--text-muted)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>First Resp.</th>
                  <th style={{ padding: '12px 16px', color: 'var(--text-muted)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Avg Res.</th>
                  <th style={{ padding: '12px 16px', color: 'var(--text-muted)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>SLA %</th>
                  <th style={{ padding: '12px 16px', color: 'var(--text-muted)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Reopen Rate</th>
                  <th style={{ padding: '12px 16px', color: 'var(--text-muted)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Last Activity</th>
                  <th style={{ padding: '12px 16px', color: 'var(--text-muted)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Performance</th>
                </tr>
              </thead>
              <tbody>
                {loading && !data ? (
                  Array.from({ length: 3 }).map((_, i) => (
                    <tr key={i}>
                      <td colSpan={9} style={{ padding: '16px' }}><Skeleton width="100%" height={24} /></td>
                    </tr>
                  ))
                ) : (
                  <AnimatePresence>
                    {engineers.map((eng, idx) => {
                      // Status Logic (Weighted Engine)
                      let loadStatus = 'Stable';
                      let loadColor = '#14b8a6';

                      const noActivity = eng.active_load === 0 && eng.resolved_count === 0 && (eng.closed_count || 0) === 0 && eng.reopened_count === 0;

                      if (noActivity) {
                        loadStatus = 'Inactive';
                        loadColor = 'var(--text-muted)';
                      } else {
                        // Weighted performance calculation out of 100
                        const slaScore = eng.sla_compliance !== null ? eng.sla_compliance : 100;
                        
                        // Relative resolved count score (max resolved in team gets 100)
                        const maxResolved = Math.max(...engineers.map(e => (e.resolved_count || 0) + (e.closed_count || 0)), 1);
                        const resolvedScore = (((eng.resolved_count || 0) + (eng.closed_count || 0)) / maxResolved) * 100;
                        
                        const reopenScore = 100 - (eng.reopen_rate !== null ? Math.min(eng.reopen_rate, 100) : 0);
                        
                        // Resolution time score (more relaxed for long-running/complex tickets)
                        let resTimeScore = 100;
                        if (eng.avg_res_time !== null) {
                          if (eng.avg_res_time <= 24) resTimeScore = 100;
                          else if (eng.avg_res_time <= 72) resTimeScore = 80;
                          else if (eng.avg_res_time <= 168) resTimeScore = 50;
                          else resTimeScore = 20;
                        }
                        
                        // Response time score (relaxed thresholds)
                        let responseScore = 100;
                        if (eng.first_response_time !== null) {
                          if (eng.first_response_time <= 4) responseScore = 100;
                          else if (eng.first_response_time <= 24) responseScore = 80;
                          else if (eng.first_response_time <= 72) responseScore = 50;
                          else responseScore = 20;
                        }

                        // SLA: 35%, Resolved Count: 30%, Reopen Rate: 20%, Response Time: 10%, Resolution Time: 5%
                        const score = (slaScore * 0.35) + (resolvedScore * 0.30) + (reopenScore * 0.20) + (responseScore * 0.10) + (resTimeScore * 0.05);

                        if (score >= 85) {
                          loadStatus = 'Excellent';
                          loadColor = '#10b981';
                        } else if (score >= 70) {
                          loadStatus = 'Good';
                          loadColor = '#3b82f6';
                        } else if (score >= 50) {
                          loadStatus = 'Stable';
                          loadColor = '#14b8a6';
                        } else if (score >= 35) {
                          loadStatus = 'Needs Attention';
                          loadColor = '#f59e0b';
                        } else {
                          loadStatus = 'Critical';
                          loadColor = '#ef4444';
                        }
                      }

                      // Format dates
                      const lastAct = eng.last_activity ? new Date(eng.last_activity).toLocaleDateString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

                      return (
                        <motion.tr 
                          key={eng.id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0 }}
                          transition={{ delay: idx * 0.05 }}
                          onClick={() => navigate(`/tickets?filter=all&timeRange=${timeRange}&engineer=${eng.id}`)}
                          style={{ borderBottom: '1px solid var(--white-05)', transition: 'background 0.2s', cursor: 'pointer' }}
                          onMouseEnter={e => e.currentTarget.style.background = 'var(--white-03)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        >
                          <td style={{ padding: '16px', fontWeight: 600, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--brand-gradient)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#000', fontSize: 14, fontWeight: 800 }}>
                              {eng.username.charAt(0).toUpperCase()}
                            </div>
                            {eng.username}
                          </td>
                          <td style={{ padding: '16px', color: 'var(--text-secondary)', fontWeight: 600 }}>{eng.active_load === 0 ? 'No Active Tickets' : `${eng.active_load} Active`}</td>
                          <td style={{ padding: '16px', color: 'var(--text-secondary)', fontWeight: 600 }}>{eng.resolved_count}</td>
                          <td style={{ padding: '16px', color: 'var(--text-secondary)' }}>{formatDuration(eng.first_response_time)}</td>
                          <td style={{ padding: '16px', color: 'var(--text-secondary)' }}>{formatDuration(eng.avg_res_time)}</td>
                          <td style={{ padding: '16px', color: eng.sla_compliance === null ? 'var(--text-muted)' : eng.sla_compliance >= 90 ? '#10b981' : eng.sla_compliance >= 70 ? '#f59e0b' : '#ef4444', fontWeight: 600 }}>
                            {eng.sla_compliance !== null ? `${eng.sla_compliance}%` : '—'}
                          </td>
                          <td style={{ padding: '16px', color: eng.reopen_rate !== null && eng.reopen_rate > 15 ? '#ef4444' : 'var(--text-secondary)' }}>
                            {eng.reopen_rate !== null ? `${eng.reopen_rate}%` : '—'}
                          </td>
                          <td style={{ padding: '16px', color: 'var(--text-secondary)', fontSize: 12 }}>{lastAct}</td>
                          <td style={{ padding: '16px' }}>
                            <span style={{ padding: '4px 10px', borderRadius: 20, background: `${loadColor}20`, color: loadColor, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', border: `1px solid ${loadColor}40`, whiteSpace: 'nowrap' }}>
                              {loadStatus}
                            </span>
                          </td>
                        </motion.tr>
                      );
                    })}
                  </AnimatePresence>
                )}
              </tbody>
            </table>
          </div>
        </motion.div>
      )}

    </motion.div>
  );
}
